import * as React from 'react'
import { FC } from 'react'
import { useGetProductPermissionsOnMount } from '@user/store/roles/hooks'
import { CreateUpdateSideDrawerWrapper } from './RoleCreateUpdate/CreateUpdateSideDrawerWrapper'
import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'
import { SuccessMedal } from '@workwave-tidal/core/icons/illustrations'
import Typography from '@mui/material/Typography'
import Button from '@mui/material/Button'
import { Add } from '@workwave-tidal/core/icons/Add'
import { useTidalThemeActive } from '@user/features/TidalThemeToggle'
import { WWMXGrid } from '@user/components/xGrid'
import { useGetColumnData } from '@user/components/Settings/Roles/hooks/useGetColumnData'
import { useData } from '@user/components/Settings/Roles/hooks/useData'
import {
  defaultColumns,
  defaultColVis,
} from '@user/components/Settings/Roles/hooks/tableColumn'
import { useActions } from '@user/components/Settings/Roles/hooks/useActions'
import InputAdornment from '@mui/material/InputAdornment'
import IconButton from '@mui/material/IconButton'
import SearchIcon from '@mui/icons-material/Search'
import TextField from '@mui/material/TextField'

export const RolesNew: FC = () => {
  const tidalThemeActive = useTidalThemeActive()

  const {
    tableRows,
    rolesLoading,
    orderBy,
    page,
    perPage,
    onDeleteRole,
    densityMode,
    onStateChange,
    sideSheetOpen,
    handleSideSheetClose,
    handleCreateEditRole,
    handleSearchChange,
    searchText,
    searchResultCount,
    roleCount,
    selectedRole,
  } = useData()

  const {
    loading: colsLoading,
    columns,
    columnVisibility,
    apiRef,
  } = useGetColumnData({
    handleEditClick: handleCreateEditRole,
    handleDeleteRequest: onDeleteRole,
    densityMode,
  })

  const {
    onColumnWidthChange,
    onSaveColumnState,
    onSortModelChange,
    sortModel,
    handlePaginationModelChange,
  } = useActions({ columns, orderBy })

  const { status: productsStatus, products } = useGetProductPermissionsOnMount()

  return (
    <Box minHeight={'calc(100svh - 78px)'} bgcolor={'#F9FAFB'}>
      <Box px={2} pt={2}>
        <Stack
          width="100%"
          direction={{ sm: 'column', md: 'row' }}
          justifyContent={{ sm: 'flex-start', md: 'space-between' }}
          bgcolor={'#E7F5F6'}
          p={1.5}
          borderRadius="4px"
          gap="15px"
          mb={1.5}
          sx={{
            border: '1px solid #E0E0E0',
          }}
        >
          <Stack display={'flex'} direction="row" alignItems="center">
            <SuccessMedal size={60} />
            <Stack ml="18px">
              <Typography variant="h6">Roles & Permissions</Typography>
              <Typography
                sx={{ color: (theme) => theme.palette.text.primary }}
                variant="body2"
              >
                Create a role for each type of user access you would like to
                enforce. You can assign the same role to more than one user.
              </Typography>
            </Stack>
          </Stack>

          <Stack
            display={'flex'}
            justifyContent={{ sm: 'flex-start', md: 'flex-end' }}
            direction="row"
            alignItems="center"
            gap={2}
            minWidth={tidalThemeActive ? '407px' : '395px'}
          >
            <Button
              sx={{ minWidth: 'fit-content', mr: '16px' }}
              color="primary"
              variant="contained"
              startIcon={<Add />}
              onClick={() => handleCreateEditRole()}
              disabled={productsStatus === 'fail'}
            >
              Add Role
            </Button>
          </Stack>
        </Stack>
      </Box>

      <TextField
        sx={{ position: 'absolute', right: '20px', zIndex: 1000 }}
        onChange={handleSearchChange}
        placeholder="Search..."
        spellCheck={false}
        variant="standard"
        type="search"
        InputProps={{
          sx: {
            padding: (theme) => `${theme.spacing(0.9)} ${theme.spacing(1.2)}`,
            '&::after': {
              borderBottom: '1px solid #',
            },
            '&::before': {
              borderBottom: '1px solid #ffffff00',
            },
            borderBottom: 'none',
          },
          startAdornment: (
            <InputAdornment position="start">
              <IconButton size="small">
                <SearchIcon />
              </IconButton>
            </InputAdornment>
          ),
        }}
      />

      <WWMXGrid
        virtualScrollerHeight={
          densityMode === 'compact'
            ? 341
            : densityMode === 'standard'
              ? 358
              : 373
        }
        applyCustomStyles={true}
        apiRef={apiRef}
        loading={rolesLoading}
        // columns
        columnVisibilityModel={columnVisibility}
        columns={colsLoading ? [] : columns}
        onColumnWidthChange={(e) =>
          onColumnWidthChange(e?.colDef, columns, columnVisibility)
        }
        columnsSelectorProps={{
          saveCallback: (e) => onSaveColumnState(e, columns),
          defaultColumns: defaultColumns,
          defaultVisibility: defaultColVis,
          lockedColumns: ['actions', 'roleName'],
        }}
        pinnedColumns={{ left: ['actions'] }}
        //state
        isDensityEnable
        density={densityMode}
        onStateChange={onStateChange}
        // rows
        rows={colsLoading ? [] : tableRows}
        rowCount={searchText !== '' ? searchResultCount : roleCount}
        // pagination
        paginationModel={{ page: page - 1, pageSize: perPage }}
        onPaginationModelChange={handlePaginationModelChange}
        pageSizeOptions={[10, 25, 50]}
        // sorting
        sortModel={sortModel}
        onSortModelChange={onSortModelChange}
        width="100%"
        hideDefaultBtn={true}
      />

      {products && sideSheetOpen && (
        <CreateUpdateSideDrawerWrapper
          open={sideSheetOpen}
          onClose={handleSideSheetClose}
          products={products}
          roleId={selectedRole?.id}
        />
      )}
    </Box>
  )
}
RolesNew.displayName = 'RolesNew'
